/**
 * Authentication service for email/passcode flow
 * Modern, friction-free authentication inspired by financial platforms
 */
import api from './api';
import { ApiResponse } from '../types';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  role: string;
  verified: boolean;
  created_at: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface SignUpRequest {
  email: string;
  company: string;
  role: string;
  firstName: string;
  lastName: string;
}

export interface SignInRequest {
  email: string;
}

export interface AuthMessageResponse {
  message: string;
  email: string;
  debugPasscode?: string;
}

export interface VerifyCodeRequest {
  email: string;
  passcode: string;
  rememberDevice?: boolean;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
}

class AuthService {
  private readonly TOKEN_KEY = 'vc_research_token';
  private readonly REFRESH_TOKEN_KEY = 'vc_research_refresh_token';
  private readonly USER_KEY = 'vc_research_user';
  private readonly DEVICE_KEY = 'vc_research_device_id';

  /**
   * Sign up new user - step 1: collect info and send verification email
   */
  async signUp(data: SignUpRequest): Promise<ApiResponse<AuthMessageResponse>> {
    try {
      const response = await api.post('/auth/signup', data);
      return { data: response.data };
    } catch (error: any) {
      // Railway quirk: Sometimes returns 404 but with valid response data
      if (error.response?.data?.message && error.response.data.email) {
        return { data: error.response.data };
      }
      return { error: error.response?.data?.message || error.response?.data?.detail || 'Sign up failed' };
    }
  }

  /**
   * Sign in existing user - step 1: send passcode to email
   */
  async signIn(data: SignInRequest): Promise<ApiResponse<AuthMessageResponse>> {
    try {
      const response = await api.post('/auth/signin', {
        ...data,
        deviceId: this.getDeviceId()
      });
      return { data: response.data };
    } catch (error: any) {
      // Railway quirk: Sometimes returns 404 but with valid response data
      if (error.response?.data?.message && error.response.data.email) {
        return { data: error.response.data };
      }
      return { error: error.response?.data?.message || error.response?.data?.detail || 'Sign in failed' };
    }
  }

  /**
   * Verify passcode - step 2: verify code and get tokens
   */
  async verifyCode(data: VerifyCodeRequest): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await api.post('/auth/verify', {
        ...data,
        deviceId: this.getDeviceId()
      });

      const authData = response.data;

      // Store auth data
      this.setAuthData(authData);

      return { data: authData };
    } catch (error: any) {
      return { error: error.response?.data?.message || 'Code verification failed' };
    }
  }

  /**
   * Resend verification code
   */
  async resendCode(email: string): Promise<ApiResponse<{ message: string; debugPasscode?: string }>> {
    try {
      const response = await api.post('/auth/resend-code', {
        email,
        deviceId: this.getDeviceId()
      });
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.message || 'Failed to resend code' };
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<ApiResponse<AuthResponse>> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/auth/refresh', {
        refreshToken,
        deviceId: this.getDeviceId()
      });

      const authData = response.data;
      this.setAuthData(authData);

      return { data: authData };
    } catch (error: any) {
      this.clearAuthData();
      return { error: error.response?.data?.message || 'Token refresh failed' };
    }
  }

  /**
   * Sign out user
   */
  async signOut(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        await api.post('/auth/signout', {
          deviceId: this.getDeviceId()
        });
      }
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      this.clearAuthData();
    }
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<ApiResponse<User>> {
    try {
      const response = await api.get('/auth/me');
      const user = response.data;

      // Update stored user data
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));

      return { data: user };
    } catch (error: any) {
      return { error: error.response?.data?.message || 'Failed to get user data' };
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(updates: Partial<User>): Promise<ApiResponse<User>> {
    try {
      const response = await api.put('/auth/profile', updates);
      const user = response.data;

      localStorage.setItem(this.USER_KEY, JSON.stringify(user));

      return { data: user };
    } catch (error: any) {
      return { error: error.response?.data?.message || 'Failed to update profile' };
    }
  }

  /**
   * Change user email (requires verification)
   */
  async changeEmail(newEmail: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await api.post('/auth/change-email', {
        email: newEmail,
        deviceId: this.getDeviceId()
      });
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.message || 'Failed to change email' };
    }
  }

  /**
   * Get stored authentication token
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get stored refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  /**
   * Get stored user data
   */
  getUser(): User | null {
    try {
      const userData = localStorage.getItem(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  /**
   * Get or generate device ID for security tracking
   */
  private getDeviceId(): string {
    let deviceId = localStorage.getItem(this.DEVICE_KEY);
    if (!deviceId) {
      deviceId = this.generateDeviceId();
      localStorage.setItem(this.DEVICE_KEY, deviceId);
    }
    return deviceId;
  }

  /**
   * Generate unique device ID
   */
  private generateDeviceId(): string {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substr(2);
    return `${timestamp}-${randomStr}`;
  }

  /**
   * Store authentication data
   */
  private setAuthData(authData: AuthResponse): void {
    localStorage.setItem(this.TOKEN_KEY, authData.token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, authData.refreshToken);
    localStorage.setItem(this.USER_KEY, JSON.stringify(authData.user));
  }

  /**
   * Clear all authentication data
   */
  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    // Note: Keep device ID for security tracking
  }

  /**
   * Setup axios interceptors for automatic token refresh
   */
  setupInterceptors(): void {
    // Add token to all requests
    api.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Handle token refresh on 401 errors
    api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const url: string = originalRequest?.url || '';

        // Endpoints that must NEVER trigger an automatic refresh:
        //   /auth/refresh  → would recurse infinitely if it 401s
        //   /auth/signin   → first attempt; nothing to refresh
        //   /auth/signup   → same
        //   /auth/verify   → user is in the passcode flow; refresh is irrelevant
        //   /auth/me       → public probe at app boot; if it 401s we're just
        //                    a logged-out visitor, refresh would loop on a
        //                    stale token left over from a previous backend
        // Anything under /auth/* is excluded for safety.
        const isAuthEndpoint = url.includes('/auth/');

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isAuthEndpoint
        ) {
          originalRequest._retry = true;

          // If we don't even have a refresh token, don't pretend to refresh
          // — clear whatever stale data is in localStorage and let the page
          // re-render in the logged-out state.
          if (!this.getRefreshToken()) {
            this.clearAuthData();
            return Promise.reject(error);
          }

          try {
            const refreshResult = await this.refreshToken();
            if (refreshResult.data) {
              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${refreshResult.data.token}`;
              return api(originalRequest);
            }
            // refreshToken() resolves with {error} on failure — fall
            // through to the rejection below instead of recursing.
            this.clearAuthData();
          } catch (refreshError) {
            // Refresh threw — clear auth and stop. Do NOT navigate from
            // the interceptor; the calling component decides what to do.
            this.clearAuthData();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }
}

export const authService = new AuthService();
export default authService;

/**
 * Append the access token as a ?token= query param. For browser
 * downloads (PDF/file <a href> navigations) that can't set an
 * Authorization header — the backend's require_app_auth accepts the
 * token from either place. Returns the URL unchanged if not logged in.
 */
export function appendAuthToken(url: string): string {
  const t = authService.getToken();
  if (!t) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(t)}`;
}
